function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable no-underscore-dangle */
import { EventTarget } from 'event-target-shim';
import { Log } from '@deephaven/log';
import { EventShimCustomEvent } from '@deephaven/utils';
import { ContextActionUtils } from "../context-actions/index.js";
var log = Log.module('Shortcut');
export var MODIFIER = /*#__PURE__*/function (MODIFIER) {
  MODIFIER["CTRL"] = "MODIFIER_CTRL";
  MODIFIER["CMD"] = "MODIFIER_CMD";
  MODIFIER["ALT"] = "MODIFIER_ALT";
  MODIFIER["OPTION"] = "MODIFIER_OPTION";
  MODIFIER["SHIFT"] = "MODIFIER_SHIFT";
  return MODIFIER;
}({});

// The value should match the KeyboardEvent.key value
// This is the value which will be displayed if no display overrides are done in the OS specific getDisplayString methods
export var KEY = /*#__PURE__*/function (KEY) {
  KEY["A"] = "A";
  KEY["B"] = "B";
  KEY["C"] = "C";
  KEY["D"] = "D";
  KEY["E"] = "E";
  KEY["F"] = "F";
  KEY["G"] = "G";
  KEY["H"] = "H";
  KEY["I"] = "I";
  KEY["J"] = "J";
  KEY["K"] = "K";
  KEY["L"] = "L";
  KEY["M"] = "M";
  KEY["N"] = "N";
  KEY["O"] = "O";
  KEY["P"] = "P";
  KEY["Q"] = "Q";
  KEY["R"] = "R";
  KEY["S"] = "S";
  KEY["T"] = "T";
  KEY["U"] = "U";
  KEY["V"] = "V";
  KEY["W"] = "W";
  KEY["X"] = "X";
  KEY["Y"] = "Y";
  KEY["Z"] = "Z";
  KEY["ZERO"] = "0";
  KEY["ONE"] = "1";
  KEY["TWO"] = "2";
  KEY["THREE"] = "3";
  KEY["FOUR"] = "4";
  KEY["FIVE"] = "5";
  KEY["SIX"] = "6";
  KEY["SEVEN"] = "7";
  KEY["EIGHT"] = "8";
  KEY["NINE"] = "9";
  KEY["BACKSPACE"] = "Backspace";
  KEY["ESCAPE"] = "Escape";
  KEY["ENTER"] = "Enter";
  KEY["DELETE"] = "Delete";
  KEY["SLASH"] = "/";
  KEY["QUESTION_MARK"] = "?";
  KEY["BACKSLASH"] = "\\";
  KEY["PIPE"] = "|";
  KEY["MINUS"] = "-";
  KEY["UNDERSCORE"] = "_";
  KEY["EQUALS"] = "=";
  KEY["PLUS"] = "+";
  KEY["BACKTICK"] = "`";
  KEY["TILDE"] = "~";
  KEY["COMMA"] = ",";
  KEY["LEFT_CHEVRON"] = "<";
  KEY["PERIOD"] = ".";
  KEY["RIGHT_CHEVRON"] = ">";
  KEY["SEMICOLON"] = ";";
  KEY["COLON"] = ":";
  KEY["SINGLE_QUOTE"] = "'";
  KEY["DOUBLE_QUOTE"] = "\"";
  KEY["LEFT_BRACKET"] = "[";
  KEY["RIGHT_BRACKET"] = "]";
  KEY["LEFT_CURLY"] = "{";
  KEY["RIGHT_CURLY"] = "}";
  KEY["F1"] = "F1";
  KEY["F2"] = "F2";
  KEY["F3"] = "F3";
  KEY["F4"] = "F4";
  KEY["F5"] = "F5";
  KEY["F6"] = "F6";
  KEY["F7"] = "F7";
  KEY["F8"] = "F8";
  KEY["F9"] = "F9";
  KEY["F10"] = "F10";
  KEY["F11"] = "F11";
  KEY["F12"] = "F12";
  return KEY;
}({});
var ALLOWED_SINGLE_KEY_SET = new Set([KEY.ENTER, KEY.DELETE, KEY.F1, KEY.F2, KEY.F3, KEY.F4, KEY.F5, KEY.F6, KEY.F7, KEY.F8, KEY.F9, KEY.F10, KEY.F11, KEY.F12]);
export default class Shortcut extends EventTarget {
  /**
   * Use to check if a keyCode corresponds to an allowed key for a shortcut
   * @param keyCode The keyCode to check. This should be the charCode of the key
   * @returns Type predicate asserting the key is an allowed KEY
   */
  static isAllowedKey(key) {
    return Object.values(KEY).includes(key);
  }

  /**
   * Checks if a KeyState has a valid key.
   * @param state KeyState with any string as the key
   * @returns True if KeyState is is using an allowed keyCode
   */
  static isValidKeyState(state) {
    var keyValue = state.keyValue;
    if (keyValue === null) {
      // Null state is valid
      return true;
    }
    if (!Shortcut.isMacPlatform && state.metaKey) {
      // MetaKey not allowed in windows
      return false;
    }
    if (!Shortcut.isAllowedKey(keyValue)) {
      return false;
    }
    var isSingleKey = !state.altKey && !state.ctrlKey && !state.metaKey && !state.shiftKey;
    return !isSingleKey || ALLOWED_SINGLE_KEY_SET.has(keyValue);
  }
  /**
   * Creates a KeyState from a valid array of modifier and key constants
   * @param keys Array of keys in the shortcut. Modifiers first and key last. Should use the MODIFIER and KEY enums
   * @returns KeyState representing the array of constants
   */
  static createKeyState(keys) {
    var keyState = {
      altKey: false,
      ctrlKey: false,
      metaKey: false,
      shiftKey: false,
      keyValue: keys[keys.length - 1]
    };
    keys.forEach(key => {
      switch (key) {
        case MODIFIER.CTRL:
          keyState.ctrlKey = true;
          break;
        case MODIFIER.ALT:
        case MODIFIER.OPTION:
          keyState.altKey = true;
          break;
        case MODIFIER.CMD:
          keyState.metaKey = true;
          break;
        case MODIFIER.SHIFT:
          keyState.shiftKey = true;
          break;
        default:
          break;
      }
    });
    return keyState;
  }
  static getKeyStateFromEvent(e) {
    var eventKey = e.key,
      keyCode = e.keyCode;
    var key = '';
    if (eventKey === 'Shift' || eventKey === 'Meta' || eventKey === 'Control' || eventKey === 'Alt') {
      key = '';
    } else if (
    // This is primarily for Mac which has a symbol keyboard hidden behind the alt key
    // The keyCode still corresponds to the letter keyCode, but the key value will be a symbol
    !Shortcut.isAllowedKey(eventKey) && Shortcut.isAllowedKey(String.fromCharCode(keyCode))) {
      key = String.fromCharCode(keyCode);
    } else {
      key = eventKey;
    }
    return {
      keyValue: key,
      altKey: e.altKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      shiftKey: e.shiftKey
    };
  }

  /**
   * Gets display string for Windows (and Linux + any other non-Mac OS)
   * If the key is not an allowed key, the display will only display the modifiers
   * @param keyState KeyState to get the display for
   * @returns The string to display on Windows/non-Mac OS
   */
  static getWindowsDisplayText(keyState) {
    var display = '';
    if (keyState.ctrlKey) {
      display += 'Ctrl+';
    }
    if (keyState.altKey) {
      display += 'Alt+';
    }
    if (keyState.shiftKey) {
      display += 'Shift+';
    }
    if (keyState.keyValue === KEY.ESCAPE) {
      display += 'Esc';
    } else if (keyState.keyValue !== null) {
      display += keyState.keyValue;
    }
    return display;
  }

  /**
   * Gets display string Mac OS
   * If the key is not an allowed key, the display will only display the modifiers
   * @param keyState KeyState to get the display for
   * @returns The string to display on Mac OS
   */
  static getMacDisplayText(keyState) {
    var display = '';
    if (keyState.ctrlKey) {
      display += '⌃';
    }
    if (keyState.altKey) {
      display += '⌥';
    }
    if (keyState.shiftKey) {
      display += '⇧';
    }
    if (keyState.metaKey) {
      display += '⌘';
    }
    switch (keyState.keyValue) {
      case KEY.ENTER:
        display += '⏎';
        break;
      case KEY.ESCAPE:
        display += '⎋';
        break;
      case KEY.BACKSPACE:
        display += '⌫';
        break;
      case KEY.DELETE:
        display += '⌦';
        break;
      case null:
        break;
      default:
        display += keyState.keyValue;
    }
    return display;
  }

  /**
   * Checks if 2 KeyStates match
   * @param state1 First KeyState to compare
   * @param state2 Second KeyState to compare
   * @returns True if the KeyStates match and have non-null keyValues
   */
  static doKeyStatesMatch(state1, state2) {
    var _state1$keyValue, _state2$keyValue;
    return ((_state1$keyValue = state1.keyValue) === null || _state1$keyValue === void 0 ? void 0 : _state1$keyValue.toUpperCase()) === ((_state2$keyValue = state2.keyValue) === null || _state2$keyValue === void 0 ? void 0 : _state2$keyValue.toUpperCase()) && state1.altKey === state2.altKey && state1.ctrlKey === state2.ctrlKey && state1.metaKey === state2.metaKey && state1.shiftKey === state2.shiftKey;
  }

  /**
   * Gets the display string for the current OS from a KeyState.
   * @param keyState KeyState to get the display for
   * @returns Display string for the current OS
   */
  static getDisplayText(keyState) {
    if (keyState.keyValue === null) {
      return '';
    }
    return Shortcut.isMacPlatform ? Shortcut.getMacDisplayText(keyState) : Shortcut.getWindowsDisplayText(keyState);
  }
  constructor(_ref) {
    var id = _ref.id,
      shortcut = _ref.shortcut,
      macShortcut = _ref.macShortcut,
      _ref$isEditable = _ref.isEditable,
      isEditable = _ref$isEditable === void 0 ? true : _ref$isEditable,
      name = _ref.name,
      tooltip = _ref.tooltip;
    super();
    _defineProperty(this, "id", void 0);
    // Unique identifier for the shortcut
    _defineProperty(this, "name", void 0);
    // e.g. Rename, Save, Run Selected
    _defineProperty(this, "tooltip", void 0);
    _defineProperty(this, "isEditable", void 0);
    _defineProperty(this, "defaultKeyState", void 0);
    _defineProperty(this, "keyState", void 0);
    this.id = id;
    this.name = name;
    this.tooltip = tooltip;
    this.isEditable = isEditable;
    var isMac = Shortcut.isMacPlatform;
    var activeShortcut = isMac ? macShortcut : shortcut;
    this.defaultKeyState = Shortcut.createKeyState(activeShortcut);
    this.keyState = this.defaultKeyState;
  }

  /**
   * Gets the display string for the current OS
   */
  getDisplayText() {
    return Shortcut.getDisplayText(this.keyState);
  }

  /**
   * Gets the current keyState for the Shortcut
   */
  getKeyState() {
    return this.keyState;
  }

  /**
   * Sets the KeyState if it is valid
   * @param keyState
   */
  setKeyState(keyState) {
    if (Shortcut.isValidKeyState(keyState)) {
      log.debug2("Shortcut ".concat(this.id, " updated to ").concat(JSON.stringify(keyState)));
      this.keyState = keyState;
      this.dispatchEvent(new EventShimCustomEvent('onUpdate', {
        detail: this
      }));
    } else {
      log.debug2("Shortcut ".concat(this.id, " tried to update to invalid keyState ").concat(JSON.stringify(keyState)));
    }
  }

  /**
   * Gets the default key state of the shortcut
   * @returns Default key state
   */
  getDefaultKeyState() {
    return this.defaultKeyState;
  }
  isDefault() {
    return Shortcut.doKeyStatesMatch(this.getDefaultKeyState(), this.getKeyState());
  }

  /**
   * Sets the shortcut to have null keyValue
   */
  setToNull() {
    this.setKeyState(Shortcut.NULL_KEY_STATE);
  }
  isNull() {
    return Shortcut.doKeyStatesMatch(this.keyState, Shortcut.NULL_KEY_STATE);
  }

  /**
   * Sets the shortcut to its default key state
   */
  setToDefault() {
    this.setKeyState(this.defaultKeyState);
  }

  /**
   * Checks if a KeyState matches the KeyState for the shortcut
   * @param keyState KeyState to check
   * @returns True if the passed KeyState matches the Shortcut's KeyState
   */
  matchesKeyState(keyState) {
    return Shortcut.doKeyStatesMatch(keyState, this.keyState);
  }

  /**
   * Alias for matchesKeyState
   * @param e KeyboardEvent to check if the Shortcut matches
   * @returns True if the event matches the Shortcut's KeyState
   */
  matchesEvent(e) {
    return this.matchesKeyState(Shortcut.getKeyStateFromEvent(e));
  }
}
_defineProperty(Shortcut, "NULL_KEY_STATE", {
  metaKey: false,
  shiftKey: false,
  altKey: false,
  ctrlKey: false,
  keyValue: null
});
_defineProperty(Shortcut, "isMacPlatform", ContextActionUtils.isMacPlatform());
//# sourceMappingURL=Shortcut.js.map